import { useEffect, useMemo, useState, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Plus, RefreshCw, Send, CreditCard, Rocket, Pencil, FlaskConical, KeyRound, Puzzle, Database, Trash2, Ban, MoreHorizontal, Search, FileSignature, Clock, Eye } from 'lucide-react'
import toast from 'react-hot-toast'
import cd2bPropuestaService from '@/services/cd2bPropuestaService'
import MiMeta from '@/components/cd2b/MiMeta'
import PageContainer from '@/components/common/PageContainer'
import PageHeader from '@/components/common/PageHeader'
import DataTable from '@/components/common/DataTable'
import { EmptyState } from '@/components/common/EmptyState'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { useConfirmacion } from '@/hooks/useConfirmacion'
import { cn } from '@/lib/utils'

// 'pagada' = cobrada, pero la empresa sigue en prueba. 'activada' = ya en producción.
const ESTADO = {
  borrador: { label: 'Borrador', variant: 'secondary' },
  enviada: { label: 'Enviada', variant: 'default' },
  vencida: { label: 'Vencida', variant: 'secondary' },
  aprobada: { label: 'Aceptada · sin pagar', variant: 'warning' },
  rechazada: { label: 'Pidió cambios', variant: 'danger' },
  pagada: { label: 'Pagada · falta arrancar', variant: 'success' },
  activada: { label: 'En producción', variant: 'accent' },
  aplicada: { label: 'Aplicada', variant: 'success' },
  cancelada: { label: 'Cancelada', variant: 'secondary' },
}

/** Filtros en chips. «Por atender» = lo que espera algo del asesor (lo mismo que su bandeja). */
const FILTROS = [
  { key: 'todas', label: 'Todas', estados: null },
  { key: 'atender', label: 'Por atender', estados: ['rechazada', 'aprobada', 'pagada'] },
  { key: 'borrador', label: 'Borradores', estados: ['borrador'] },
  { key: 'enviada', label: 'Enviadas', estados: ['enviada', 'rechazada', 'vencida'] },
  { key: 'aprobada', label: 'Aceptadas', estados: ['aprobada'] },
  { key: 'pagada', label: 'Pagadas', estados: ['pagada'] },
  { key: 'activada', label: 'En producción', estados: ['activada', 'aplicada'] },
  { key: 'cancelada', label: 'Canceladas', estados: ['cancelada'] },
]

const estadoDe = (p) => p.estadoEfectivo || p.estado
const sinAcentos = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

const nombreCliente = (u) => {
  if (!u) return '—'
  const n = `${u.firstName || ''} ${u.lastName || ''}`.trim()
  return n || u.email || '—'
}

/**
 * ¿El cliente ha llegado a ENTRAR alguna vez con el correo que se tecleó aquí?
 *
 * Su cuenta de acceso NACE al crear la cotización, con el correo que escribe el asesor y sin
 * verificarlo. Si tiene una letra mal, la cuenta funciona pero no le llega a nadie —tampoco el
 * «Olvidé mi contraseña»—. Este aviso lo pone a la vista en la misma pantalla del error.
 * `lastLogin` es la prueba fuerte (entró de verdad); `emailVerified` cubre a quien verificó.
 */
const AvisoCuentaCliente = ({ u }) => {
  if (!u || u.lastLogin || u.emailVerified) return null
  return (
    <p className="mt-0.5 text-caption leading-tight text-warning-700 dark:text-warning-300">
      Aún no ha entrado: revisa que {u.email} sea correcto
    </p>
  )
}

const fmt = (n, m = 'USD') =>
  typeof n === 'number' ? `$ ${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${m && m !== 'USD' ? ` ${m}` : ''}` : '—'

/** Qué puede hacerse con la cotización en su estado. Una sola regla para la tabla y las tarjetas. */
function permisos(p) {
  const estado = estadoDe(p)
  const tieneDemo = !!p.demo?.tenantId
  return {
    estado,
    tieneDemo,
    editable: ['borrador', 'enviada', 'rechazada', 'vencida'].includes(estado),
    enviable: ['borrador', 'enviada', 'rechazada', 'vencida'].includes(estado),
    // Se cobra DESPUÉS de que el cliente acepta, y nunca dos veces (docs/180).
    cobrable: estado === 'aprobada',
    linkVivo: estado === 'aprobada' && ((p.pago?.url && !p.pago?.pagadoAt && !p.pago?.anuladoAt) || p.stripe?.paymentLinkUrl),
    producible: estado === 'pagada',
    demoable: p.modalidad === 'nube' && !['pagada', 'activada', 'aplicada', 'cancelada'].includes(estado) && !tieneDemo,
    // Borrar solo lo que nunca salió de casa y no tiene tenant detrás (el backend manda igual: 409).
    borrable: ['borrador', 'rechazada', 'cancelada'].includes(estado) && !tieneDemo,
    cancelable: ['enviada', 'aprobada'].includes(estado),
    pideDemo: p.solicitudPrueba?.at && !p.solicitudPrueba.atendidaAt,
  }
}

export default function ConsultorCotizaciones() {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const [propuestas, setPropuestas] = useState([])
  const [loading, setLoading] = useState(true)
  const [q, setQ] = useState('')
  const filtro = FILTROS.find((f) => f.key === params.get('estado')) ? params.get('estado') : 'todas'
  const { confirmar, pedirDatos, dialogo } = useConfirmacion()

  const cargar = useCallback(async () => {
    setLoading(true)
    try {
      const res = await cd2bPropuestaService.getAll()
      setPropuestas(Array.isArray(res?.data) ? res.data : [])
    } catch (e) {
      toast.error(e?.response?.data?.error || 'No se pudieron cargar las cotizaciones')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { cargar() }, [cargar])

  const conteo = useMemo(() => {
    const c = {}
    for (const f of FILTROS) c[f.key] = f.estados ? propuestas.filter((p) => f.estados.includes(estadoDe(p))).length : propuestas.length
    return c
  }, [propuestas])

  const filas = useMemo(() => {
    const f = FILTROS.find((x) => x.key === filtro)
    const n = sinAcentos(q.trim())
    return propuestas.filter((p) =>
      (!f?.estados || f.estados.includes(estadoDe(p))) &&
      (!n || sinAcentos(`${p.empresaNombre} ${nombreCliente(p.userId)} ${p.userId?.email || ''}`).includes(n)))
  }, [propuestas, filtro, q])

  const elegirFiltro = (k) => {
    const sig = new URLSearchParams(params)
    if (k === 'todas') sig.delete('estado'); else sig.set('estado', k)
    setParams(sig, { replace: true })
  }

  const accion = async (fn, okMsg) => {
    try {
      const res = await fn()
      if (res?.data?.negocioCodigo) {
        // Empresa demo creada / acceso: código de empresa + PIN para que el DUEÑO entre a probarla.
        const usr = res.data.usuarioAdmin ? ` · Usuario: ${res.data.usuarioAdmin}` : ''
        const pin = res.data.pinAdmin ? ` · PIN: ${res.data.pinAdmin}` : ''
        try { await navigator.clipboard.writeText(res.data.negocioCodigo) } catch { /* noop */ }
        toast.success(`Empresa demo · Código: ${res.data.negocioCodigo}${usr}${pin} (código copiado; compártelo con el dueño)`, { duration: 14000 })
      } else {
        toast.success(okMsg)
      }
      cargar()
    } catch (e) {
      toast.error(e?.response?.data?.error || 'La acción falló')
    }
  }

  /** Borrar es IRREVERSIBLE: la confirmación nombra la empresa y el precio. */
  const eliminar = async (p) => {
    const ok = await confirmar({
      titulo: `¿Eliminar ${p.empresaNombre || 'esta cotización'}?`,
      descripcion: `${fmt(p.precioMensual, p.moneda)}/mes. No se puede deshacer.`,
      textoConfirmar: 'Eliminar',
      variante: 'destructiva',
    })
    if (ok) accion(() => cd2bPropuestaService.eliminar(p._id), 'Cotización eliminada')
  }

  const cancelar = async (p) => {
    const datos = await pedirDatos({
      titulo: `Cancelar ${p.empresaNombre || 'esta cotización'}`,
      descripcion: 'El link que tiene el dueño deja de servir y no se podrá pagar. Queda en el registro.',
      campos: [{ clave: 'motivo', etiqueta: 'Motivo (opcional)', obligatorio: false, multilinea: true }],
      textoConfirmar: 'Cancelar cotización',
      textoCancelar: 'Volver',
    })
    if (datos === null) return
    accion(() => cd2bPropuestaService.cancelar(p._id, datos.motivo || ''), 'Cotización cancelada')
  }

  /**
   * Pasar a producción: la empresa de prueba deja de serlo. Antes se consulta la lista de
   * verificación, porque hay cosas que no se pueden deshacer (los datos de prueba se mezclarían
   * con los reales).
   */
  const pasarAProduccion = async (p) => {
    let chequeo = null
    try {
      const res = await cd2bPropuestaService.listoParaProduccion(p._id)
      chequeo = res?.data
    } catch (e) {
      toast.error(e?.response?.data?.error || 'No se pudo revisar el estado de la empresa')
      return
    }
    if (chequeo?.bloqueos?.length) {
      toast.error(chequeo.bloqueos.map((b) => b.mensaje).join(' · '), { duration: 12000 })
      navigate(`/consultor/cotizaciones/${p._id}?paso=4&seccion=produccion`)
      return
    }
    const avisos = (chequeo?.advertencias || []).map((a) => `• ${a.mensaje}`).join('\n')
    const ok = await confirmar({
      titulo: `¿Pasar ${p.empresaNombre} a producción?`,
      descripcion: `La empresa de prueba se convierte en la real.${avisos ? `\n\nAntes de seguir:\n${avisos}` : ''}`,
      textoConfirmar: 'Pasar a producción',
    })
    if (ok) await accion(() => cd2bPropuestaService.pasarAProduccion(p._id), `${p.empresaNombre} ya está en producción`)
  }

  const solicitarPlugin = async (p) => {
    const datos = await pedirDatos({
      titulo: 'Solicitar un plugin a medida',
      descripcion: `Para ${p.empresaNombre || 'esta cotización'}. Lo recibe el equipo de desarrollo de cd-2b.`,
      campos: [
        { clave: 'titulo', etiqueta: 'Título' },
        { clave: 'descripcion', etiqueta: '¿Qué debe hacer?', multilinea: true },
      ],
      textoConfirmar: 'Enviar solicitud',
    })
    if (!datos) return
    try {
      await cd2bPropuestaService.solicitarPlugin(p._id, datos)
      toast.success('Solicitud de plugin enviada al equipo')
    } catch (e) {
      toast.error(e?.response?.data?.error || 'No se pudo enviar la solicitud')
    }
  }

  const enviar = (p, tieneDemo) => accion(
    () => cd2bPropuestaService.enviarAlDueno(p._id),
    tieneDemo ? 'Cotización y acceso a la demo enviados por correo' : 'Cotización enviada por correo',
  )

  /** La acción que toca AHORA según el estado: un solo botón visible; el resto va al menú ⋯. */
  function accionPrincipal(p, className) {
    const x = permisos(p)
    const abrir = (q = '') => navigate(`/consultor/cotizaciones/${p._id}${q}`)
    if (x.producible) return <Button size="xs" className={className} onClick={() => pasarAProduccion(p)}><Rocket size={15} />Arrancar</Button>
    if (x.cobrable) return <Button size="xs" className={className} onClick={() => abrir('?paso=4&seccion=cobro')}><CreditCard size={15} />Cobrar</Button>
    if (x.pideDemo) return <Button size="xs" className={className} onClick={() => abrir('?paso=4&seccion=demo')}><FlaskConical size={15} />{p.solicitudPrueba.tipo === 'extension' ? 'Extender demo' : 'Crear demo'}</Button>
    if (x.estado === 'rechazada') return <Button size="xs" variant="outline" className={className} onClick={() => abrir()}><Pencil size={15} />Editar</Button>
    if (x.estado === 'borrador') return <Button size="xs" variant="outline" className={className} onClick={() => enviar(p, x.tieneDemo)}><Send size={15} />Enviar</Button>
    if (x.enviable) return <Button size="xs" variant="outline" className={className} onClick={() => enviar(p, x.tieneDemo)}><Send size={15} />Reenviar</Button>
    return <Button size="xs" variant="outline" className={className} onClick={() => abrir()}><Eye size={15} />Abrir</Button>
  }

  function menuAcciones(p) {
    const x = permisos(p)
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon-sm" aria-label={`Más acciones de ${p.empresaNombre || 'la cotización'}`} onClick={(e) => e.stopPropagation()}>
            <MoreHorizontal size={18} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuItem onClick={() => navigate(`/consultor/cotizaciones/${p._id}`)}>
            {x.editable ? <Pencil size={16} /> : <Eye size={16} />} {x.editable ? 'Editar' : 'Abrir'}
          </DropdownMenuItem>
          {x.enviable && (
            <DropdownMenuItem onClick={() => enviar(p, x.tieneDemo)}>
              <Send size={16} /> {x.tieneDemo ? 'Enviar al dueño (con acceso a la demo)' : 'Enviar al dueño por correo'}
            </DropdownMenuItem>
          )}
          {x.demoable && (
            <DropdownMenuItem onClick={() => accion(() => cd2bPropuestaService.crearDemo(p._id), 'Creando empresa demo…')}>
              <FlaskConical size={16} /> Crear empresa demo
            </DropdownMenuItem>
          )}
          {x.tieneDemo && (
            <>
              <DropdownMenuItem onClick={() => accion(() => cd2bPropuestaService.accesoDemo(p._id), 'Acceso demo')}>
                <KeyRound size={16} /> Ver acceso del dueño a la demo
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`/consultor/cotizaciones/${p._id}/migracion`)}>
                <Database size={16} /> Migrar datos del sistema anterior
              </DropdownMenuItem>
            </>
          )}
          <DropdownMenuItem onClick={() => solicitarPlugin(p)}>
            <Puzzle size={16} /> Solicitar un plugin a medida
          </DropdownMenuItem>
          {(x.borrable || x.cancelable) && <DropdownMenuSeparator />}
          {x.cancelable && (
            <DropdownMenuItem onClick={() => cancelar(p)} className="text-danger-700 dark:text-danger-300">
              <Ban size={16} /> Cancelar cotización
            </DropdownMenuItem>
          )}
          {x.borrable && (
            <DropdownMenuItem onClick={() => eliminar(p)} className="text-danger-700 dark:text-danger-300">
              <Trash2 size={16} /> Eliminar
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  function estadoCelda(p) {
    const x = permisos(p)
    const e = ESTADO[x.estado] || { label: x.estado, variant: 'secondary' }
    const desc = p.descuento
    const descPend = Number(desc?.monto) > 0 && ['pendiente', 'rechazado'].includes(desc?.aprobacion?.estado || 'pendiente')
    return (
      <div className="flex flex-col items-start gap-1 md:items-start">
        <Badge variant={e.variant}>{e.label}</Badge>
        {x.linkVivo && <span className="text-caption text-warning-700 dark:text-warning-300">Link enviado · esperando pago</span>}
        {x.pideDemo && <span className="text-caption text-accent-700 dark:text-accent-300">{p.solicitudPrueba.tipo === 'extension' ? 'Pide más días de prueba' : 'Quiere probar primero'}</span>}
        {descPend && (
          <span className={cn('text-caption', desc?.aprobacion?.estado === 'rechazado' ? 'text-danger-700 dark:text-danger-300' : 'text-warning-700 dark:text-warning-300')}>
            {desc?.aprobacion?.estado === 'rechazado' ? `Descuento rechazado${desc.aprobacion.motivo ? `: ${desc.aprobacion.motivo}` : ''}` : 'Descuento por aprobar'}
          </span>
        )}
      </div>
    )
  }

  const movimiento = (p) => {
    const estado = estadoDe(p)
    if (estado === 'enviada' && p.vigenteHasta) {
      const d = Math.ceil((new Date(p.vigenteHasta).getTime() - Date.now()) / 86400000)
      if (d <= 3) return <span className="inline-flex items-center gap-1 text-danger-700 dark:text-danger-300"><Clock size={13} />{d <= 1 ? 'Vence mañana' : `Vence en ${d} días`}</span>
    }
    return p.updatedAt ? new Date(p.updatedAt).toLocaleDateString('es', { day: 'numeric', month: 'short' }) : '—'
  }

  const columnas = [
    {
      clave: 'empresa', titulo: 'Empresa', prioridad: 'titulo',
      celda: (p) => (
        <div className="min-w-0">
          <p className="truncate font-semibold text-foreground">{p.empresaNombre || '—'}</p>
          <p className="truncate text-caption text-subtle">{nombreCliente(p.userId)}{p.pais ? ` · ${p.pais}` : ''}</p>
          {!p.pais && <p className="text-caption text-warning-700 dark:text-warning-300">Sin país</p>}
          <AvisoCuentaCliente u={p.userId} />
        </div>
      ),
    },
    { clave: 'edicion', titulo: 'Edición', prioridad: 'meta', celda: (p) => <span>{p.edicion || '—'}<span className="text-subtle">{p.modalidad ? ` · ${p.modalidad}` : ''}</span></span> },
    { clave: 'mensual', titulo: 'Mensual', alineacion: 'derecha', prioridad: 'valor', celda: (p) => <span className="font-semibold tabular-nums text-foreground">{fmt(p.precioMensual, p.moneda)}</span> },
    { clave: 'estado', titulo: 'Estado', prioridad: 'valor', celda: (p) => estadoCelda(p) },
    { clave: 'mov', titulo: 'Movimiento', prioridad: 'meta', celda: (p) => <span className="text-body-sm text-subtle">{movimiento(p)}</span> },
    {
      clave: 'acciones', titulo: 'Acción', alineacion: 'derecha', prioridad: 'acciones',
      celda: (p) => (
        <div className="flex w-full items-center justify-end gap-1.5 md:w-auto" onClick={(e) => e.stopPropagation()}>
          {accionPrincipal(p, 'flex-1 md:flex-none')}
          {menuAcciones(p)}
        </div>
      ),
    },
  ]

  return (
    <PageContainer>
      <PageHeader
        title="Cotizaciones"
        description="Arma el paquete, envíalo al dueño, cobra y arráncalo."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={cargar} aria-label="Actualizar">
              <RefreshCw size={18} />
            </Button>
            <Button className="hidden md:inline-flex" onClick={() => navigate('/consultor/cotizaciones/nueva')}>
              <Plus size={16} /> Nueva cotización
            </Button>
          </div>
        }
      />

      <MiMeta />

      <div className="space-y-3">
        <div className="relative">
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-subtle" aria-hidden="true" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar empresa, dueño o correo" aria-label="Buscar cotización" className="pl-9" />
        </div>
        <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 no-scrollbar sm:mx-0 sm:flex-wrap sm:px-0" role="tablist" aria-label="Filtrar por estado">
          {FILTROS.filter((f) => f.key === 'todas' || conteo[f.key] > 0 || f.key === filtro).map((f) => {
            const on = filtro === f.key
            return (
              <button
                key={f.key}
                type="button"
                role="tab"
                aria-selected={on}
                onClick={() => elegirFiltro(f.key)}
                className={cn(
                  'inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border px-3.5 text-body-sm font-medium transition-colors',
                  on ? 'border-primary bg-primary text-white' : 'border-line bg-card text-foreground hover:border-neutral-300 dark:hover:border-neutral-600'
                )}
              >
                {f.label}
                <span className={cn('tabular-nums', on ? 'text-white/80' : 'text-subtle')}>{conteo[f.key]}</span>
              </button>
            )
          })}
        </div>
      </div>

      <DataTable
        etiquetaTabla="Cotizaciones"
        columnas={columnas}
        filas={filas}
        claveDeFila={(p) => p._id}
        cargando={loading}
        tarjetasEnMovil
        onFilaClick={(p) => navigate(`/consultor/cotizaciones/${p._id}`)}
        className="bg-card"
        vacio={
          <EmptyState
            icono={<FileSignature size={24} />}
            titulo={propuestas.length ? 'Nada con ese filtro' : 'Aún no tienes cotizaciones'}
            descripcion={propuestas.length ? 'Prueba con otro estado o borra la búsqueda.' : 'Crea la primera: eliges edición y módulos, y se la envías al dueño.'}
            accion={!propuestas.length && <Button size="sm" onClick={() => navigate('/consultor/cotizaciones/nueva')}><Plus size={16} />Nueva cotización</Button>}
          />
        }
      />
      {dialogo}
    </PageContainer>
  )
}
